import { useState } from "react";
import { X, Search } from "lucide-react";
import { DropdownClassData } from "../../../../services/workPermitFormService";
import { WorkerForm } from "../../../../types/workpermit";

interface WorkerSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeOptions: DropdownClassData[];
  unselectedLocalWorkers?: { worker: WorkerForm; globalIndex: number }[];
  selectedWorkerNames: Set<string>;
  onSelectEmployee: (emp: DropdownClassData) => void;
  onSelectLocalWorker?: (globalIndex: number) => void;
}

export default function WorkerSelectModal({
  isOpen,
  onClose,
  employeeOptions = [],
  unselectedLocalWorkers = [],
  selectedWorkerNames,
  onSelectEmployee,
  onSelectLocalWorker,
}: WorkerSelectModalProps) {
  const [search, setSearch] = useState("");

  if (!isOpen) return null;

  const filteredApiList = employeeOptions.filter((emp) =>
    emp.DataLabel.toLowerCase().includes(search.toLowerCase()) ||
    (emp.DataCode && emp.DataCode.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredLocalList = unselectedLocalWorkers.filter(({ worker }) =>
    worker.PersonnelName.toLowerCase().includes(search.toLowerCase()) ||
    worker.IdentityNumber.includes(search)
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
      {/* Background blur backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-3xl w-full max-w-3xl shadow-2xl z-10 border border-gray-100 dark:border-gray-700 overflow-hidden transform transition-all duration-300 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-base font-extrabold text-gray-900 dark:text-white">
              Pilih Pekerja
            </h3>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full transition"
          >
            <X className="w-5 h-5 stroke-[2.5]" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari pekerja berdasarkan nama atau KTP..."
              className="w-full pl-10 pr-4 py-2 text-xs bg-slate-50 dark:bg-gray-750 border border-slate-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:text-white transition-all font-semibold"
            />
          </div>
        </div>

        {/* Grid Content (3 columns: grid-cols-1 sm:grid-cols-3) */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {filteredApiList.length === 0 && filteredLocalList.length === 0 ? (
            <div className="text-center py-12 text-slate-400 dark:text-slate-500 text-xs italic">
              Tidak ada pekerja yang ditemukan.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {filteredApiList.map((emp) => {
                const isSelected = selectedWorkerNames.has(emp.DataLabel.trim().toLowerCase());
                return (
                  <div
                    key={`api_${emp.DataId}`}
                    onClick={() => {
                      if (!isSelected) {
                        onSelectEmployee(emp);
                      }
                    }}
                    className={`p-3 rounded-xl border text-left transition-all duration-200 flex flex-col justify-between select-none ${
                      isSelected
                        ? "bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 opacity-75 cursor-default"
                        : "bg-white dark:bg-gray-750 border-slate-200 dark:border-gray-600 hover:border-blue-500 hover:bg-slate-50 dark:hover:bg-gray-700 cursor-pointer shadow-2xs"
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                          {emp.DataLabel}
                        </span>
                        {isSelected && (
                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 px-1.5 py-0.5 rounded">
                            Terpilih
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400 mt-1 truncate">
                        {emp.DataCode ? emp.DataCode : "-"}
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredLocalList.map(({ worker, globalIndex }) => {
                const isSelected = selectedWorkerNames.has(worker.PersonnelName.trim().toLowerCase());
                return (
                  <div
                    key={`local_${globalIndex}`}
                    onClick={() => {
                      if (!isSelected && onSelectLocalWorker) {
                        onSelectLocalWorker(globalIndex);
                      }
                    }}
                    className={`p-3 rounded-xl border text-left transition-all duration-200 flex flex-col justify-between select-none ${
                      isSelected
                        ? "bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 opacity-75 cursor-default"
                        : "bg-white dark:bg-gray-750 border-slate-200 dark:border-gray-600 hover:border-blue-500 hover:bg-slate-50 dark:hover:bg-gray-700 cursor-pointer shadow-2xs"
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                          {worker.PersonnelName}
                        </span>
                        {isSelected && (
                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 px-1.5 py-0.5 rounded">
                            Terpilih
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400 mt-1 truncate">
                        {worker.IdentityNumber ? worker.IdentityNumber : "-"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end bg-slate-50/50 dark:bg-gray-800/50">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs active:scale-98"
          >
            Selesai
          </button>
        </div>
      </div>
    </div>
  );
}
